import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export interface ApprovalRow {
  id: string;
  workUnitId: string;
  ctrlNo: string;
  projectId: string;
  projectName: string;
  projectIsLegacy: boolean;
  workflowName: string;
  departmentId: string;
  departmentName: string;
  stepId: string;
  stepName: string;
  submittedById: string;
  submittedAt: string | null;
  supportingText: string | null;
  valueText: string | null;
  valueNumber: string | null;
  approvalStatus: "pending" | "approved" | "rejected" | null;
  resolutionNote: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  proposedRollbackToStepId: string | null;
  proposedRollbackToStepName: string | null;
  proposedRollbackToWorkflowId: string | null;
  proposedRollbackToWorkflowName: string | null;
  proposedRollbackByName: string | null;
  proposedRollbackAt: string | null;
  proposedRollbackReason: string | null;
  attachmentId: string | null;
  attachmentFilename: string | null;
  attachmentContentType: string | null;
  caption: string | null;
}

export function useApprovals(
  scope: "pending" | "history",
  opts: { enabled?: boolean; decision?: "approved" | "rejected" | "" } = {},
) {
  const decision = opts.decision || "";
  return useQuery<{ results: ApprovalRow[] }>({
    queryKey: ["approvals", scope, { decision }],
    queryFn: () =>
      api
        .get<{ results: ApprovalRow[] }>("/approvals", {
          params: {
            scope,
            decision: decision || undefined,
          },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
  });
}

export function useBulkApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ completionIds, note }: { completionIds: string[]; note?: string }) =>
      api
        .post<{ approved: string[]; failed: { id: string; reason: string }[] }>(
          "/approvals/bulk-approve",
          { completionIds, note },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["work-units"] });
    },
  });
}

/** Per-row approve/reject from the queue page. Accepts WU + step IDs at call time. */
export function useResolveApprovalFromQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workUnitId: string;
      stepId: string;
      decision: "approved" | "rejected";
      note?: string;
      rejectedToStepId?: string | null;
    }) =>
      api
        .post(`/work-units/${input.workUnitId}/steps/${input.stepId}/resolve-approval`, {
          decision: input.decision,
          note: input.note ?? "",
          rejected_to_step_id: input.rejectedToStepId ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["work-units"] });
    },
  });
}
